import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import FortuneResult from "../components/FortuneResult";
import { useLanguage } from "../contexts/LanguageContext";
import { DateTime } from "luxon";
import { analyzeBazi } from "../api/bazi";
import { getUserProfile } from "../api/user";
import { computeBaziPillars } from "../utils/computeBaziPillars";

export default function FortunePage({ session }) {
  const { language } = useLanguage();
  const [analysisMode, setAnalysisMode] = useState("hybrid"); // hybrid, gpt_only, rag_only
  const [resultsData, setResultsData] = useState({
    trueSolarTime: null,
    baziPillars: null,
    prediction: "",
    language: "en",
    formData: null
  });
  const [isLoading, setIsLoading] = useState(true);

  const parsePillar = (pillarString) => {
    if (!pillarString || pillarString.length < 2) {
      return { stem: '', branch: '' };
    }
    if (pillarString.includes('(')) {
      const parenIndex = pillarString.indexOf('(');
      const pinyinPart = pillarString.substring(parenIndex + 1, pillarString.length - 1);
      const pinyinWords = pinyinPart.split(' ');
      const stemPinyin = pinyinWords[0];
      const branchPinyin = pinyinWords[1];
      return { stem: stemPinyin, branch: branchPinyin };
    } else if (pillarString.includes(' ')) {
      const parts = pillarString.split(' ');
      return { stem: parts[0], branch: parts[1] };
    } else {
      const stem = pillarString.charAt(0);
      const branch = pillarString.charAt(1);
      return { stem, branch };
    }
  };

  const calculateBazi = useCallback((dateStr, gender) => {
    if (!dateStr) return null;
    
    let dt = DateTime.fromISO(dateStr, { setZone: true });
    if (!dt.isValid) {
      dt = DateTime.fromSQL(dateStr);
    }

    if (dt.isValid) {
      dt = DateTime.fromObject({
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
        second: dt.second
      }, { zone: 'utc' });

      const pillars = computeBaziPillars(dt, language);
      return {
        baziPillars: pillars,
        trueSolarTime: dateStr,
        formData: { gender }
      };
    }
    return null;
  }, [language]);

  // Ref to track if we are currently fetching to prevent double execution
  const fetchingRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadBaseData = async () => {
      // Only set loading if we don't have data or are invalidating
      setIsLoading(true); 
      
      let dataToUse = null;

      // 1. Try sessionStorage
      try {
        const stored = sessionStorage.getItem('baziResults');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Only use if language matches
          if (parsed.language === language) {
             dataToUse = {
                trueSolarTime: parsed.trueSolarTime ? DateTime.fromISO(parsed.trueSolarTime, { setZone: true }) : null,
                baziPillars: parsed.baziPillars || null,
                prediction: parsed.prediction || "",
                language: parsed.language || language,
                formData: parsed.formData || null
             };
          }
        }
      } catch (e) {
        console.warn("Storage parse error", e);
      }

      // 2. If no valid stored data, calculate from profile
      if ((!dataToUse || !dataToUse.baziPillars) && session) {
         try {
            const profile = await getUserProfile(session.access_token);
            if (profile?.date_of_birth) {
                const calculated = calculateBazi(profile.date_of_birth, profile.gender_at_birth || "male");
                if (calculated) {
                    dataToUse = {
                        ...calculated,
                        prediction: "", // No prediction yet
                        language: language
                    };
                }
            }
         } catch (e) {
            console.warn("Profile fetch error", e);
         }
      }

      if (isMounted) {
          if (dataToUse && dataToUse.baziPillars) {
              setResultsData(dataToUse);
              // If we have data but no prediction, keep loading true to prevent UI flash
              if (dataToUse.prediction) {
                  setIsLoading(false);
              }
          } else {
              // No data available at all
              setResultsData({
                  trueSolarTime: null,
                  baziPillars: null,
                  prediction: "",
                  language: language,
                  formData: null
              });
              setIsLoading(false);
          }
      }
    };

    loadBaseData();
    
    return () => { isMounted = false; };
  }, [session, language, calculateBazi]);

  // Separate effect for fetching prediction
  useEffect(() => {
    const { baziPillars, prediction } = resultsData;
    
    // Only fetch if we have pillars, but NO prediction
    if (baziPillars && !prediction) {
        const signature = JSON.stringify(baziPillars) + '-' + language + '-' + analysisMode;
        
        // Prevent duplicate calls / infinite loops
        if (fetchingRef.current === signature) return;
        
        fetchingRef.current = signature;
        setIsLoading(true);

        const fetchPrediction = async () => {
             try {
                const { yearPillar, monthPillar, dayPillar, hourPillar } = baziPillars;
                const yp = parsePillar(yearPillar);
                const mp = parsePillar(monthPillar);
                const dp = parsePillar(dayPillar);
                const hp = parsePillar(hourPillar);

                const result = await analyzeBazi(
                    yp.stem, yp.branch,
                    mp.stem, mp.branch,
                    dp.stem, dp.branch,
                    hp.stem, hp.branch,
                    language,
                    analysisMode
                );
                
                // Only update if signature matches (component didn't change context)
                if (fetchingRef.current === signature) {
                    setResultsData(prev => {
                        const newData = { ...prev, prediction: result };
                        
                        // Handle trueSolarTime safely (it might be a string or a DateTime object)
                        const tst = newData.trueSolarTime;
                        const tstString = (tst && typeof tst.toISO === 'function') ? tst.toISO() : tst;

                        sessionStorage.setItem('baziResults', JSON.stringify({
                            ...newData,
                            trueSolarTime: tstString
                        }));
                        return newData;
                    });
                }
             } catch (err) {
                 console.error("Prediction fetch failed", err);
             } finally {
                 if (fetchingRef.current === signature) {
                     setIsLoading(false);
                     // Clear ref to allow retry if needed (though dependency array prevents immediate retry)
                     fetchingRef.current = null;
                 }
             }
        };

        fetchPrediction();
    }
  }, [resultsData, language, analysisMode]);

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setAnalysisMode(newMode);
    // Clear prediction to trigger re-fetch
    setResultsData(prev => ({
        ...prev,
        prediction: ""
    }));
  };

  return (
    <DashboardLayout session={session}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

        {resultsData.baziPillars ? (
            <>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '0.9rem', color: '#666' }}>
                    {language === 'en' ? 'Analysis Mode:' : '分析模式:'}
                </label>
                <select 
                    value={analysisMode} 
                    onChange={handleModeChange}
                    style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                    }}
                >
                    <option value="hybrid">{language === 'en' ? 'Hybrid (RAG + GPT)' : '混合 (RAG + GPT)'}</option>
                    <option value="gpt_only">{language === 'en' ? 'Pure GPT' : '纯 GPT'}</option>
                    <option value="rag_only">{language === 'en' ? 'Local Knowledge (RAG Only)' : '本地知识库 (仅 RAG)'}</option>
                </select>
            </div>
            <FortuneResult 
              prediction={resultsData.prediction} 
              isLoading={isLoading} 
              language={language} 
            />
            </>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
            {isLoading ? (
              <p>{language === "en" ? "Loading..." : "加载中..."}</p>
            ) : (
              <p>{language === "en" ? "No data available. Please update your profile." : "暂无数据，请更新个人资料。"}</p>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
